import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, message, Button, Input, Select, Upload, Space, Popconfirm } from 'antd';
import { isEmpty } from 'utils/utils';
import { useRefCode, useLecturerDetail } from 'hooks';
import { UploadOutlined, CloseOutlined, PaperClipOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/lib/upload/interface';
import { isFileViewer } from 'utils/file-utils';
import { IMaskInput } from "react-imask";
import { makeMaskPhoneNumber } from "utils/form-utils"

export interface FileInfoType {
    fileid: number, 
    filename: string    
}

export interface LecturerEditModalProps extends PopupProps {
    id: number;
    onDownload?: (fileSeq:number, fileName: string) => void;
    onFilePreview?: (fileSeq:number, fileName: string, seq: number, gubun: string, editable: boolean) => void; 
}

const LecturerEditModal = ({ isOpen, onClose, onDownload, onFilePreview, id}: LecturerEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    

    const [accountFileList, setAccountFileList] = useState<UploadFile[]>([]);
    const [accountFile, setAccountFile] = useState<FileInfoType>({fileid: 0, filename: ''});  

    const [identityFileList, setIdentityFileList] = useState<UploadFile[]>([]);
    const [identityFile, setIdentityFile] = useState<FileInfoType>({fileid: 0, filename: ''});  

    const [profileFileList, setProfileFileList] = useState<UploadFile[]>([]);            
    const [profileFile, setProfileFile] = useState<FileInfoType>({fileid: 0, filename: ''});  

  /* ======================================================
  * hooks
  * ====================================================== */
  const onUpdateCompleted = () => {
    onClose?.(true);
  } 

  const { refCodeList: lecturer_category } = useRefCode({field:'lecturer_category1'});  
  const { lecturerInfo, lecturerInsert, lecturerUpdate, lecturerDelete } = useLecturerDetail({id:id, onUpdateCompleted: onUpdateCompleted}); 
   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;

   /* ======================================================
   * events
   * ====================================================== */

    const onCancel = () => {
        onClose?.(false);
    };
        
    const onSubmit = (e) => {
        if (id > 0) {
            lecturerUpdate({
                id: id,
                name: e.name,
                lecturer_type: e.lecturer_type,
                lecturer_category: e.lecturer_category,   
                tel: e.tel,   
                phone: e.phone, 
                email: e.email,
                price: form.getFieldValue("price").toString().replaceAll(',',''),
                memo: e.memo,
                income_type: e.income_type,
                account_fileData: accountFileList.map((file) => file.originFileObj),
                identity_fileData: identityFileList.map((file) => file.originFileObj),
                profile_fileData: profileFileList.map((file) => file.originFileObj),
                account_number_file_id: accountFile.fileid,
                identity_number_file_id: identityFile.fileid,
                profile_file_id: profileFile.fileid,
              })
        } else {
            lecturerInsert({
                id: id,
                name: e.name,
                lecturer_type: e.lecturer_type,
                lecturer_category: e.lecturer_category,   
                tel: e.tel,   
                phone: e.phone, 
                email: e.email,
                price: form.getFieldValue("price").toString().replaceAll(',',''),
                memo: e.memo,
                income_type: e.income_type,
                account_fileData: accountFileList.map((file) => file.originFileObj),
                identity_fileData: identityFileList.map((file) => file.originFileObj),
                profile_fileData: profileFileList.map((file) => file.originFileObj),
                account_number_file_id: accountFile.fileid,
                identity_number_file_id: identityFile.fileid,
                profile_file_id: profileFile.fileid,
              })
        }
    };

   
    /* ======================================================
    * methods
    * ====================================================== */
    const checkPrice = (_: any, value) => {
        if (value > -1) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Price must be greater than zero!'));
    };   
       
  /* ======================================================
   * effects
   * ====================================================== */

    useEffect(() => {
        if (!!lecturerInfo) {
            form.setFieldsValue({
                name: lecturerInfo.name,
                email: lecturerInfo.email,
                phone: lecturerInfo.phone,
                tel: lecturerInfo.tel,
                lecturer_type: lecturerInfo.lecturer_type,
                lecturer_category: lecturerInfo.lecturer_category,
                price: lecturerInfo.price,
                income_type: lecturerInfo.income_type,
                memo: lecturerInfo.memo,
            });
        }

    }, [lecturerInfo]);

    return (
        <ModalWrapper open={isOpen} onCancel={onCancel} width={550} title={id===0?'강사 등록':'강사 수정'} closable>
            <Form
                form={form}
                name="LecturerInfo"
                layout="horizontal"  
                labelCol={{ flex: '100px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Form.Item name="name" label={'강사명'} rules={[{ required: true, message: '' }]}>
                    <Input type="text" placeholder='강사명을 입력하세요'/>                    
                </Form.Item>  
                <Form.Item name="lecturer_type" label={'강사구분'} rules={[{ required: true, message: '' }]}>
                    <Select>
                        <Select.Option value="IN">내부강사</Select.Option>
                        <Select.Option value="OUT">외부강사</Select.Option>
                    </Select>                                        
                </Form.Item>  
                <Form.Item name="lecturer_category" label={'강사분류'}>
                    <Select>
                    {lecturer_category?.map((item) => {
                        return (<Select.Option key={item.name} value={item.name}>{item.value}</Select.Option>)
                    })}                        
                    </Select>                                        
                </Form.Item>                                       
                <Form.Item name="tel" label={'전화번호'}>
                <Input placeholder='전화번호를 입력하세요' onChange={(e) => form.setFieldValue('tel', makeMaskPhoneNumber(e.target.value))} />                 
                </Form.Item>                     
                <Form.Item name="phone" label={'핸드폰'}>
                <IMaskInput mask={'000-0000-0000'} className={'ant-input'} placeholder='핸드폰번호를 입력하세요'/>                   
                </Form.Item>                  
                <Form.Item name="email" label={'이메일'} rules={[{ type: 'email', message: '' }]}>
                    <Input type="text" placeholder='이메일을 입력하세요'/>                    
                </Form.Item>  
                <Form.Item name="account_number_file_id" label={'통장사본'}>
                    <Space direction="vertical">
                        <Upload
                            fileList={accountFileList}
                            onChange={({ fileList: list }) => {
                                setAccountFileList(list);
                            }} 
                            beforeUpload={(file, filelist) => {
                                if ((file.size as number) >= 10 * 1024 * 1024) {
                                  message.warn('파일 용량 초과', 1);
                                  return Upload.LIST_IGNORE;
                                }
                            }}                                                       
                            customRequest={() => true}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png"
                            showUploadList={false}
                            disabled={accountFile.fileid > 0}
                        >
                            <Button icon={<UploadOutlined />} style={{fontSize:'12px'}}>등록</Button>
                        </Upload>        
                        {(accountFile.fileid > 0) && (
                            <Space key={accountFile.fileid}>
                                <Button onClick={()=>{onDownload && onDownload(accountFile.fileid, accountFile.filename)}} shape="round" icon={<DownloadOutlined />} size='small' style={{padding:'0px 3px'}}/>
                                {isFileViewer(accountFile.filename) && (<Button onClick={()=>{onFilePreview && onFilePreview(accountFile.fileid, accountFile.filename, id, 'account', false)}} shape="round" icon={<SearchOutlined />} size='small' style={{padding:'0px 3px'}}/>)}                                   

                                {accountFile.filename}
                                <CloseOutlined
                                    style={{ fontSize: 12, color: '#b9b9b9' }}
                                    onClick={() => {
                                        setAccountFile({fileid:0, filename:''});
                                    }}
                                />
                            </Space>
                        )} 
                        {accountFileList?.map(({ uid, name }) => (
                            <Space key={uid}>
                            <PaperClipOutlined /><span style={{width: '200px'}}>{name}</span>
                            <CloseOutlined
                                style={{ fontSize: 12, color: '#b9b9b9' }}
                                onClick={() => {
                                    setAccountFileList(accountFileList.map((file) => file).filter((file) => file.uid !== uid));
                                }}
                            />
                            </Space>
                        ))}                                               
                    </Space>                                        
                </Form.Item>                  
                <Form.Item name="identity_number_file_id" label={'발행서류'}>
                <Space direction="vertical">
                        <Upload
                            fileList={identityFileList}
                            onChange={({ fileList: list }) => {
                                setIdentityFileList(list);
                            }} 
                            beforeUpload={(file, filelist) => {
                                if ((file.size as number) >= 10 * 1024 * 1024) {
                                  message.warn('파일 용량 초과', 1);
                                  return Upload.LIST_IGNORE;
                                }
                            }}                                                       
                            customRequest={() => true}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png"
                            showUploadList={false}
                            disabled={identityFile.fileid > 0}
                        >
                            <Button icon={<UploadOutlined />} style={{fontSize:'12px'}}>등록</Button>
                        </Upload>        
                        {(identityFile.fileid > 0) && (
                            <Space key={identityFile.fileid}>
                                <Button onClick={()=>{onDownload && onDownload(identityFile.fileid, identityFile.filename)}} shape="round" icon={<DownloadOutlined />} size='small' style={{padding:'0px 3px'}}/>
                                {isFileViewer(identityFile.filename) && (<Button onClick={()=>{onFilePreview && onFilePreview(identityFile.fileid, identityFile.filename, id, 'identity', false)}} shape="round" icon={<SearchOutlined />} size='small' style={{padding:'0px 3px'}}/>)}                                   

                                {identityFile.filename}
                                <CloseOutlined
                                    style={{ fontSize: 12, color: '#b9b9b9' }}
                                    onClick={() => {
                                        setIdentityFile({fileid:0, filename:''});
                                    }}
                                />
                            </Space>
                        )} 
                        {identityFileList?.map(({ uid, name }) => (
                            <Space key={uid}>
                            <PaperClipOutlined /><span style={{width: '200px'}}>{name}</span>
                            <CloseOutlined
                                style={{ fontSize: 12, color: '#b9b9b9' }}
                                onClick={() => {
                                    setIdentityFileList(identityFileList.map((file) => file).filter((file) => file.uid !== uid));
                                }}
                            />
                            </Space>
                        ))}                                               
                    </Space>                                       
                </Form.Item>      
                <Form.Item name="profile_file_id" label={'프로필'}>
                    <Space direction="vertical">
                        <Upload
                            fileList={profileFileList}
                            onChange={({ fileList: list }) => {
                                setProfileFileList(list);
                            }} 
                            beforeUpload={(file, filelist) => {
                                if ((file.size as number) >= 10 * 1024 * 1024) {
                                  message.warn('파일 용량 초과', 1);
                                  return Upload.LIST_IGNORE;
                                }
                            }}                                                       
                            customRequest={() => true}
                            maxCount={1}
                            accept=".pdf,.jpg,.jpeg,.png"
                            showUploadList={false}
                            disabled={profileFile.fileid > 0}
                        >
                            <Button icon={<UploadOutlined />} style={{fontSize:'12px'}}>등록</Button>
                        </Upload>        
                        {(profileFile.fileid > 0) && (
                            <Space key={profileFile.fileid}>
                                <Button onClick={()=>{onDownload && onDownload(profileFile.fileid, profileFile.filename)}} shape="round" icon={<DownloadOutlined />} size='small' style={{padding:'0px 3px'}}/>
                                {isFileViewer(profileFile.filename) && (<Button onClick={()=>{onFilePreview && onFilePreview(profileFile.fileid, profileFile.filename, id, 'profile', false)}} shape="round" icon={<SearchOutlined />} size='small' style={{padding:'0px 3px'}}/>)}                                   

                                {profileFile.filename}
                                <CloseOutlined
                                    style={{ fontSize: 12, color: '#b9b9b9' }}
                                    onClick={() => {
                                        setProfileFile({fileid:0, filename:''})
                                    }}
                                />
                            </Space>
                        )} 
                        {profileFileList?.map(({ uid, name }) => (
                            <Space key={uid}>
                            <PaperClipOutlined /><span style={{width: '200px'}}>{name}</span>
                            <CloseOutlined
                                style={{ fontSize: 12, color: '#b9b9b9' }}
                                onClick={() => {
                                    setProfileFileList(profileFileList.map((file) => file).filter((file) => file.uid !== uid));
                                }}
                            />
                            </Space>
                        ))}                                               
                    </Space>
                </Form.Item>                                                
                <Form.Item name="price" label={'시간당 단가'} rules={[{ required: true, validator: checkPrice, message: '' }]}>
                <IMaskInput 
                    min={0} 
                    mask = {Number}
                    thousandsSeparator={','}
                    className={'ant-input'} 
                    unmask={'typed'} 
                    onAccept = {((value, mask) => form.setFieldValue('price', value))}/>                
                </Form.Item> 
                <Form.Item name="income_type" label={'소득형태'}>
                    <Select>
                        <Select.Option value="사업소득">사업소득</Select.Option>
                        <Select.Option value="기타소득">기타소득</Select.Option>
                        <Select.Option value="세금계산서">세금계산서</Select.Option>
                    </Select>                                        
                </Form.Item>                  
                <Form.Item name="memo" label={'메모'}>
                    <TextArea rows={3} placeholder='메모를 입력하세요'/>                    
                </Form.Item>                  
                <ButtonGroup className={'buttonsWrapper'}>
                    <Button onClick={onCancel}>취소</Button>
                    <div>
                        {(id>0) && (
                            <Popconfirm
                                title="삭제 하시겠습니까?"
                                okText="삭제"
                                cancelText="취소"
                                onConfirm={() => lecturerDelete({id:id})}
                            >   
                                <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                            </Popconfirm>
                        )}
                        <Button htmlType="submit"  type="primary">{id===0?'등록':'수정'}</Button>
                    </div>
                </ButtonGroup>                
            </Form>
        </ModalWrapper>  
    )    
};

export default React.memo(LecturerEditModal);    